import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ReceiptText, ExternalLink, CheckCircle2, AlertTriangle, Clock, Award } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate, fmtMoney } from "@/lib/format";
import { usePageTitle } from "@/hooks/usePageTitle";

// Mis facturas: las facturas de Armony que vienen de Odoo (solo con la integración activa).
export default function MisFacturas() {
  usePageTitle("Mis facturas");
  const [data, setData] = useState<any>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.get("/customer/invoices").then((r) => setData(r.data)).catch(() => setFailed(true));
  }, []);

  if (failed) return <p className="text-sm text-red-700" role="alert">No pudimos cargar tus facturas. Inténtalo de nuevo en unos minutos.</p>;
  if (!data) return <div className="flex justify-center h-96 items-center" role="status"><Loader2 className="w-7 h-7 text-gold animate-spin" aria-hidden="true" /><span className="sr-only">Cargando…</span></div>;

  const pending = data.items.filter((i: any) => i.amount_residual > 0.009);
  const overdue = pending.filter((i: any) => i.overdue);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoices-page">
      <div>
        <h1 className="text-3xl font-serif font-medium">Mis facturas</h1>
        <p className="text-sm text-slate-500 mt-1">Tus facturas de Armony. Pagar a tiempo te mantiene al día y suma puntos.</p>
      </div>

      {!data.enabled ? (
        <div className="card-obsidian p-12 text-center">
          <ReceiptText className="w-10 h-10 text-slate-500 mx-auto mb-4" aria-hidden="true" />
          <p className="text-slate-600">Tus facturas todavía no están disponibles aquí.</p>
          <Link to="/portal/soporte" className="text-sm text-navy underline mt-2 inline-block">¿Necesitas ayuda?</Link>
        </div>
      ) : (
        <>
          {overdue.length > 0 ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3" role="status">
              <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-slate-800">
                Tienes {overdue.length === 1 ? "una factura vencida" : `${overdue.length} facturas vencidas`} por{" "}
                <strong>{fmtMoney(overdue.reduce((t: number, i: any) => t + i.amount_residual, 0), overdue[0].currency)}</strong>.
                Mientras esté pendiente, los canjes pueden estar restringidos.
              </p>
            </div>
          ) : (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex gap-3" role="status">
              <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-slate-800">Estás al día con tus pagos. ¡Gracias!</p>
            </div>
          )}

          {data.items.length === 0 ? (
            <div className="card-obsidian p-12 text-center text-slate-600">Aún no tienes facturas.</div>
          ) : (
            <ul className="space-y-3">
              {data.items.map((i: any) => {
                const isPaid = i.amount_residual <= 0.009;
                return (
                  <li key={i.id} className="card-obsidian p-5 flex flex-wrap items-center gap-4" data-testid="invoice-row">
                    <div className={`w-11 h-11 rounded-full grid place-items-center shrink-0 ${isPaid ? "bg-emerald-500/15" : i.overdue ? "bg-amber-500/15" : "bg-blue-500/15"}`}>
                      {isPaid ? <CheckCircle2 className="w-5 h-5 text-emerald-700" aria-hidden="true" />
                        : i.overdue ? <AlertTriangle className="w-5 h-5 text-amber-700" aria-hidden="true" />
                        : <Clock className="w-5 h-5 text-blue-700" aria-hidden="true" />}
                    </div>
                    <div className="flex-1 min-w-[12rem]">
                      <p className="font-semibold text-slate-900">{i.number}</p>
                      <p className="text-sm text-slate-600">
                        Emitida el {fmtDate(i.invoice_date)} · {isPaid ? `Pagada${i.paid_at ? ` el ${fmtDate(i.paid_at)}` : ""}` : `Vence el ${fmtDate(i.due_date)}`}
                      </p>
                      {isPaid && i.punctual && (
                        <p className="text-xs text-gold-ink font-medium mt-1 inline-flex items-center gap-1"><Award className="w-3.5 h-3.5" aria-hidden="true" /> Pagada a tiempo</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-mono font-semibold text-slate-900">{fmtMoney(isPaid ? i.amount_total : i.amount_residual, i.currency)}</p>
                      <p className={`text-xs font-medium ${isPaid ? "text-emerald-700" : i.overdue ? "text-amber-700" : "text-slate-600"}`}>
                        {i.overdue ? "Vencida" : isPaid ? "Pagada" : i.payment_label}
                      </p>
                    </div>
                    {i.portal_url && (
                      <a href={i.portal_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-navy font-medium hover:underline">
                        Ver factura <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" /><span className="sr-only">(se abre en otra pestaña)</span>
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
